import { Box, Button, Skeleton, Tooltip, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import { getExamListsByCode } from "../../../../../api/services/exam";
import { handleApiError } from "../../../../../utils/errorHandler";
import { pushNotification } from "../../../../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider";
import { NotificationTypes } from "../../../../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider.types";
import { E_ATTEMPT_MAX_REACHED } from "../../../../../utils/constants";
import { useNavigate } from "react-router-dom";

interface TakeExamDetailsProps {
    examCodeId: string;
    examCode: string;
    examCodeName: string;
    examCodeDesc: string;
}

interface ExamItemProps {
    _id: string;
    exam: string;
    locked?: boolean;
    attempts: number;
    maxAttempts: number;
    items: number;
    mins: number;
    countdown?: { value: string; label: string }[];
}

const TakeExamDetails: React.FC<TakeExamDetailsProps> = ({ examCodeId, examCode, examCodeName, examCodeDesc }) => {
    const [currentExams, setCurrentExams] = useState<ExamItemProps[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    useEffect(() => {
        const run = async () => {
            if (!examCodeId) return;
            setLoading(true);
            try {
                const examLists = await getExamListsByCode(examCodeId);

                const formattedExams = examLists.data.map((exam) => {
                    const now = new Date();
                    const start = new Date(exam.startDate);
                    const isLocked = start.getTime() > now.getTime();

                    let countdownArray;

                    if (isLocked) {
                        let remainingSeconds = Math.floor((start.getTime() - now.getTime()) / 1000);

                        const days = Math.floor(remainingSeconds / 86400);
                        remainingSeconds %= 86400;

                        const hrs = Math.floor(remainingSeconds / 3600);
                        remainingSeconds %= 3600;

                        const mins = Math.floor(remainingSeconds / 60);
                        const secs = remainingSeconds % 60;

                        countdownArray = [
                            { label: "DAYS", value: days.toString().padStart(2, "0") },
                            { label: "HRS", value: hrs.toString().padStart(2, "0") },
                            { label: "MINS", value: mins.toString().padStart(2, "0") },
                            { label: "SEC", value: secs.toString().padStart(2, "0") }
                        ];
                    }

                    return {
                        _id: exam._id,
                        exam: exam.name,
                        locked: isLocked,
                        attempts: exam.attempts,
                        maxAttempts: exam.maxAttempts,
                        items: exam.numberOfQuestions,
                        mins: exam.timer,
                        countdown: countdownArray
                    };
                });
                setCurrentExams(formattedExams);
            } catch (error) {
                handleApiError(error, {
                    notify: (msg) => {
                        pushNotification({
                            details: msg,
                            type: NotificationTypes.Error,
                        });
                    },
                });
            } finally {
                setLoading(false);
            }

        }
        run();
    }, [examCodeId]);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 400, color: '#5840BA', mb: 2 }}>Exam Code:</Typography>
                <Box
                    sx={{
                        background: '#C4B5FF40',
                        border: '2px dashed #B8A9F7',
                        borderRadius: '8px',
                        padding: '12px 16px',
                        textAlign: 'center',
                    }}
                >
                    <Typography
                        sx={{
                            fontWeight: 700,
                            fontSize: '24px',
                            color: '#5840BA',
                        }}
                    >
                        {examCode}
                    </Typography>
                </Box>
            </Box>
            <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 400, color: '#5840BA', mb: 1 }}>Description:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 400, color: '#818181' }}>{examCodeDesc}</Typography>
            </Box>

            <Box>
                <Typography variant="body2" sx={{ fontWeight: 400, color: '#5840BA', my: 2 }}>List of Exams</Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>

                    {loading ? (
                        Array.from({ length: 3 }).map((_, idx) => (
                            <Skeleton key={idx} variant="rectangular" height={74} animation="wave" />
                        ))
                    ) : currentExams.length === 0 ? (
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                py: 5,
                            }}
                        >
                            {/* <Box
                                component="img"
                                src={EMPTY_STATE_ILLUSTRATION_URL}
                                alt="No Exams Illustration"
                                sx={{
                                    width: 200,
                                    height: 200,
                                    mb: 2,
                                    opacity: 0.85,
                                }}
                            /> */}
                            <Typography
                                variant="body2"
                                sx={{
                                    color: "#818181",
                                    fontWeight: 500,
                                    fontSize: "12px",
                                    mt: 0.5,
                                }}
                            >
                                No data to display
                            </Typography>
                        </Box>
                    ) : currentExams.map((exam) => (
                        <ExamItem
                            _id={exam._id}
                            key={exam.exam}
                            exam={exam.exam}
                            attempts={exam.attempts}
                            maxAttempts={exam.maxAttempts}
                            items={exam.items}
                            mins={exam.mins}
                            countdown={exam.countdown}
                            locked={exam.locked}
                        />
                    ))}
                </Box>
            </Box>
        </Box>
    );
};


const ExamItem: React.FC<ExamItemProps> = ({
    _id,
    exam,
    locked,
    attempts,
    maxAttempts,
    items,
    mins,
    countdown
}) => {
    const navigate = useNavigate();

    const handleTakeExam = (examId: string) => {
        if (attempts === maxAttempts) {
            pushNotification({
                details: E_ATTEMPT_MAX_REACHED,
                type: NotificationTypes.Error,
            });
        } else {
            navigate(`/exam/${examId}`);
        }
    }
    // Convert countdown array to total seconds – FIXED
    const toSeconds = (cd: any[]) => {
        let days = parseInt(cd.find(x => x.label === "DAYS")?.value ?? 0);
        let hrs = parseInt(cd.find(x => x.label === "HRS")?.value ?? 0);
        let mins = parseInt(cd.find(x => x.label === "MINS")?.value ?? 0);
        let sec = parseInt(cd.find(x => x.label === "SEC")?.value ?? 0);

        return (days * 86400) + (hrs * 3600) + (mins * 60) + sec;
    };

    const [remaining, setRemaining] = useState(
        locked && countdown ? toSeconds(countdown) : 0
    );

    // Run countdown every second
    useEffect(() => {
        if (!locked) return;
        if (remaining <= 0) return;

        const interval = setInterval(() => {
            setRemaining(prev => Math.max(prev - 1, 0));
        }, 1000);

        return () => clearInterval(interval);
    }, [remaining, locked]);

    // Format remaining seconds into display – FIXED
    const formatCountdown = () => {
        let sec = remaining;

        const days = Math.floor(sec / 86400);
        sec %= 86400;

        const hrs = Math.floor(sec / 3600);
        sec %= 3600;

        const mins = Math.floor(sec / 60);
        const secs = sec % 60;

        return [
            {
                label: days === 1 ? "DAY" : "DAYS",
                value: days.toString().padStart(2, "0"),
            },
            {
                label: hrs === 1 ? "HR" : "HRS",
                value: hrs.toString().padStart(2, "0"),
            },
            {
                label: mins === 1 ? "MIN" : "MINS",
                value: mins.toString().padStart(2, "0"),
            },
            { label: "SEC", value: secs.toString().padStart(2, "0") }
        ];
    };

    const formatted = formatCountdown();
    const isUnlocked = !locked || remaining <= 0;

    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                p: '12px',
                borderRadius: 2,
                border: "1px solid #D8D8DD",
                backgroundColor: locked ? "#F6F6F6" : "#fff",
                maxHeight: '100px'
            }}
        >
            {/* Left side */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Box
                    sx={{
                        width: 36,
                        height: 36,
                        borderRadius: "8px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#A8A8A8"
                    }}
                >
                    {isUnlocked ? <DescriptionOutlinedIcon /> : <LockOutlinedIcon />}
                </Box>

                <Box>
                    <Typography sx={{ fontWeight: 500, fontSize: 14, color: "#373737" }}>
                        {exam}
                    </Typography>

                    <Typography sx={{ fontWeight: 400, fontSize: 12, color: "#6F6F6F" }}>
                        {items} items &nbsp; &nbsp; {mins} mins &nbsp; &nbsp; {attempts} of {maxAttempts} attempt
                    </Typography>
                </Box>
            </Box>

            {/* Right side */}
            {isUnlocked ? (
                <Tooltip
                    title={E_ATTEMPT_MAX_REACHED}
                    disableHoverListener={attempts !== maxAttempts}
                >
                    <span>
                        <Button
                            variant="contained"
                            sx={{
                                width: "85px",
                                height: "31px",
                                fontSize: "12px",
                                textTransform: "none",
                                borderRadius: "4px",
                                backgroundColor: "#5840BA",
                                whiteSpace: "nowrap",
                                minWidth: "85px"
                            }}
                            disabled={attempts === maxAttempts}
                            onClick={() => handleTakeExam(_id)}
                        >
                            Take exam
                        </Button>
                    </span>
                </Tooltip>
            ) : (
                <Box
                    sx={{
                        p: "6px 10px",
                        borderRadius: 2,
                        backgroundColor: "#fff",
                        border: "1px solid #D8D8DD",
                        textAlign: "center",
                        display: "flex",
                        gap: 2
                    }}
                >
                    {formatted.map((c, index) => (
                        <Box key={index}>
                            <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#373737" }}>
                                {c.value}
                            </Typography>
                            <Typography sx={{ fontSize: 7, fontWeight: 400, color: "#373737" }}>
                                {c.label}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            )}
        </Box>
    );
};


export default TakeExamDetails;